import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchOrder, OrderItem, OrderResponse, updateOrder, addItemToCart, updateItemInCart } from '../../../api/orders';
import { fetchProductVariants, ProductVariant } from '../../../api/productVariants';
import { fetchInventories } from '../../../api/inventories';
import Notification from '../../../components/midone/components/Base/Notification';
import { removeOrderCartItem } from '../../../api/orders';
import type { NotificationElement } from '../../../components/midone/components/Base/Notification';
import PromotionDetailsModal from '../../../components/PromotionDetailsModal';

interface OrderProductServicesCartProps {
  orderId: string | null;
  showHeader?: boolean;
  hideContinueButton?: boolean;
  onBackToOrders?: () => void;
  onReturnToIntake?: (orderId: string | null) => void;
  onCompleted?: (orderId: string) => void;
  onOpenRelatedProducts?: () => void;
  onOpenRecommendedProducts?: () => void;
}

type CartItem = {
  localId: string;
  id?: string;
  productVariantId: string | null;
  productVariant?: ProductVariant | null;
  description: string | null;
  itemType: string | null;
  quantity: number;
  unitPrice: number;
  discountTotal: number;
  taxTotal: number;
  cartWideDiscountId?: string | null;
  itemWiseDiscountId?: string | null;
  groupId?: number | null;
  metadata: Record<string, unknown> | null;
  technicianId: string | null;
  sortOrder?: number | null;
};

type ToastState = {
  id: number;
  title: string;
  message: string;
};

const generateLocalId = (prefix: string) => `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

const toNumber = (value: string | number | null | undefined, fallback = 0): number => {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? fallback : value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  if (value === null || value === undefined) {
    return fallback;
  }

  return fallback;
};

const roundCurrency = (value: number) => Number(value.toFixed(2));
const roundQuantity = (value: number) => Number(value.toFixed(2));
const TOAST_DURATION = 2500;

const mapOrderItemToCartItem = (item: OrderItem): CartItem => ({
  localId: item.id ?? generateLocalId('order-item'),
  id: item.id,
  productVariantId: item.productVariantId,
  productVariant: item.productVariant ?? undefined,
  description: item.description,
  itemType: item.itemType,
  quantity: roundQuantity(toNumber(item.quantity, 1)),
  unitPrice: roundCurrency(toNumber(item.unitPrice, 0)),
  discountTotal: roundCurrency(toNumber(item.discountTotal, 0)),
  taxTotal: roundCurrency(toNumber(item.taxTotal, 0)),
  cartWideDiscountId: (item as any).cartWideDiscountId ?? null,
  itemWiseDiscountId: (item as any).itemWiseDiscountId ?? null,
  groupId: (item as any).groupId ?? null,
  metadata: item.metadata ?? null,
  technicianId: item.technicianId ?? null,
  sortOrder: item.sortOrder ?? null,
});

const mapVariantToCartItem = (variant: ProductVariant): CartItem => ({
  localId: generateLocalId('variant'),
  productVariantId: variant.id,
  productVariant: variant,
  description: variant.name,
  itemType: null,
  quantity: 1,
  unitPrice: roundCurrency(toNumber(variant.retailPrice, 0)),
  discountTotal: 0,
  taxTotal: 0,
  metadata: null,
  technicianId: null,
});

const OrderProductServicesCart: FC<OrderProductServicesCartProps> = ({
  orderId,
  showHeader = true,
  hideContinueButton = false,
  onBackToOrders,
  onReturnToIntake,
  onCompleted,
  onOpenRelatedProducts,
  onOpenRecommendedProducts,
}) => {
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [variantResults, setVariantResults] = useState<ProductVariant[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [variantStockByErpId, setVariantStockByErpId] = useState<Record<number, string | null>>({});
  const [recentlySelectedVariant, setRecentlySelectedVariant] = useState<ProductVariant | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastRef = useRef<NotificationElement | null>(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [cartRefreshing, setCartRefreshing] = useState(false);

  const [promotionModalOpen, setPromotionModalOpen] = useState(false);
  const [promotionModalId, setPromotionModalId] = useState<string | null>(null);

  // Remove cart modal state (abandoned cart handled server-side)
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeModalFor, setRemoveModalFor] = useState<CartItem | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [removeReminderDays, setRemoveReminderDays] = useState<string>('');
  const [removeSaving, setRemoveSaving] = useState(false);

  const loadOrder = useCallback(
    async (id: string) => {
      const data = await fetchOrder(id);
      setOrder(data);
      setCartItems((data.items ?? []).map(mapOrderItemToCartItem));
      setDirty(false);
    },
    [],
  );

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setCartItems([]);
      setOrderError('Order ID not specified.');
      setOrderLoading(false);
      return;
    }

    let isMounted = true;
    setOrderLoading(true);
    setOrderError(null);

    loadOrder(orderId)
      .catch((error) => {
        if (!isMounted) return;
        const message = error instanceof Error ? error.message : 'Failed to load order details.';
        setOrder(null);
        setOrderError(message);
        setCartItems([]);
        setDirty(false);
      })
      .finally(() => {
        if (!isMounted) return;
        setOrderLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [orderId, loadOrder]);

  // Allow external callers (e.g., AI recommendations modal) to trigger a cart refresh
  // and optionally show a toast in this cart context.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<any>).detail as
        | { orderId?: string; toast?: ToastState }
        | undefined;
      const targetOrderId = detail?.orderId;
      if (!orderId || (targetOrderId && targetOrderId !== orderId)) return;
      setCartRefreshing(true);
      loadOrder(orderId)
        .catch(() => {
          // ignore errors here; main cart already has its own error handling
        })
        .finally(() => {
          setCartRefreshing(false);
        });

      if (detail?.toast) {
        setToast({
          id: Date.now(),
          title: detail.toast.title,
          message: detail.toast.message,
        });
      }
    };

    window.addEventListener('order-cart-refresh', handler as EventListener);
    return () => window.removeEventListener('order-cart-refresh', handler as EventListener);
  }, [orderId, loadOrder]);

  useEffect(() => {
    const trimmed = search.trim();

    if (!trimmed) {
      setDebouncedSearch('');
      setVariantResults([]);
      setSearchError(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    setVariantResults([]);
    setSearchError(null);

    const handle = window.setTimeout(() => setDebouncedSearch(trimmed), 350);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    if (!debouncedSearch) {
      setVariantResults([]);
      setSearchError(null);
      setSearchLoading(false);
      setVariantStockByErpId({});
      return;
    }

    let isMounted = true;
    setSearchLoading(true);
    setSearchError(null);

    fetchProductVariants({ page: 1, limit: 8, search: debouncedSearch, isActive: true })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        // Only show variants that are active and whose master (for services) is active on the ERP side.
        const filtered = response.data.filter((variant) => variant.isActive !== false);
        setVariantResults(filtered);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        const message =
          error instanceof Error ? error.message : 'Failed to search product variants.';
        setVariantResults([]);
        setSearchError(message);
      })
      .finally(() => {
        if (isMounted) {
          setSearchLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [debouncedSearch]);

  // Fetch stock for the listed variants for the order's selected location
  useEffect(() => {
    const locId = order?.locationId ?? null;
    if (!locId || variantResults.length === 0) return;
    let active = true;

    const missing = variantResults
      .map((v) => v.erpId)
      .filter((id) => typeof id === 'number' && !(id in variantStockByErpId)) as number[];

    if (missing.length === 0) return;

    // Fetch sequentially to keep it simple and avoid flooding
    (async () => {
      const updates: Record<number, string | null> = {};
      for (const erpId of missing) {
        try {
          const inv = await fetchInventories({ page: 1, limit: 1, variantErpId: erpId, locationId: locId });
          updates[erpId] = inv.data[0]?.stock ?? null;
        } catch {
          updates[erpId] = null;
        }
      }
      if (active && Object.keys(updates).length) {
        setVariantStockByErpId((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => { active = false; };
  }, [order?.locationId, variantResults]);

  useEffect(() => {
    if (!recentlySelectedVariant) {
      return;
    }

    const handle = window.setTimeout(() => setRecentlySelectedVariant(null), 4000);
    return () => window.clearTimeout(handle);
  }, [recentlySelectedVariant]);

  useEffect(() => {
    if (!toast) {
      return;
    }

    toastRef.current?.showToast?.();

    const handle = window.setTimeout(() => {
      toastRef.current?.hideToast?.();
      setToast(null);
    }, TOAST_DURATION);

    return () => {
      window.clearTimeout(handle);
      toastRef.current?.hideToast?.();
    };
  }, [toast?.id]);

  const showVariantDropdown = useMemo(
    () => search.trim().length > 0,
    [search],
  );

  const handleSelectVariant = async (variant: ProductVariant) => {
    if (!orderId) return;
    setSaveError(null);
    setAddingToCart(true);
    try {
      const updated = await addItemToCart(orderId, {
        productVariantId: variant.id,
        description: variant.name,
        quantity: 1,
        unitPrice: toNumber(variant.retailPrice, 0),
      });
      setOrder(updated);
      setCartItems((updated.items ?? []).map(mapOrderItemToCartItem));
      setDirty(false);
      setRecentlySelectedVariant(variant);
      setToast({ id: Date.now(), title: 'Success', message: 'Added to cart.' });
      setSearch('');
      setDebouncedSearch('');
      setVariantResults([]);
      setSearchError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to add item to cart.';
      setSaveError(msg);
    } finally {
      setAddingToCart(false);
    }
  };

  const itemTotals = useMemo(() => {
    if (!cartItems.length) {
      return {
        subtotal: 0,
        discount: 0,
        tax: 0,
        total: 0,
      };
    }

    const subtotal = cartItems.reduce(
      (acc, item) => acc + item.quantity * item.unitPrice,
      0,
    );
    const itemLevelDiscount = cartItems.reduce((acc, item) => acc + item.discountTotal, 0);
    const orderLevelDiscount = toNumber(order?.discountTotal ?? 0, 0);
    const discount = itemLevelDiscount > 0 ? itemLevelDiscount : orderLevelDiscount;
    const tax = cartItems.reduce((acc, item) => acc + item.taxTotal, 0);
    const total = subtotal - discount + tax;

    return {
      subtotal,
      discount,
      tax,
      total,
    };
  }, [cartItems, order?.discountTotal]);

  const totalUnits = useMemo(
    () => cartItems.reduce((acc, item) => acc + item.quantity, 0),
    [cartItems],
  );

  const formatAmount = (value: string | number | null | undefined) => {
    const numeric = typeof value === 'number' ? value : Number(value ?? 0);

    if (Number.isNaN(numeric)) {
      return '0.00';
    }

    return numeric.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatQuantity = (value: number) => {
    if (Number.isNaN(value)) {
      return '0';
    }

    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  };

  const handleIncreaseQuantity = async (localId: string) => {
    const item = cartItems.find((i) => i.localId === localId);
    if (!item || !orderId || !item.id) return;
    const nextQty = roundQuantity(item.quantity + 1);
    setSaveError(null);
    setCartRefreshing(true);
    try {
      const updated = await updateItemInCart(orderId, item.id, { orderItemId: item.id, quantity: nextQty });
      setOrder(updated);
      setCartItems((updated.items ?? []).map(mapOrderItemToCartItem));
      setDirty(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to update quantity.';
      setSaveError(msg);
    } finally {
      setCartRefreshing(false);
    }
  };

  const handleDecreaseQuantity = async (localId: string) => {
    const item = cartItems.find((i) => i.localId === localId);
    if (!item || !orderId || !item.id) return;
    const nextQty = roundQuantity(Math.max(item.quantity - 1, 1));
    setSaveError(null);
    setCartRefreshing(true);
    try {
      const updated = await updateItemInCart(orderId, item.id, { orderItemId: item.id, quantity: nextQty });
      setOrder(updated);
      setCartItems((updated.items ?? []).map(mapOrderItemToCartItem));
      setDirty(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to update quantity.';
      setSaveError(msg);
    } finally {
      setCartRefreshing(false);
    }
  };

  const handleQuantityChange = async (localId: string, value: string) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return;
    }

    const nextQuantity = roundQuantity(Math.max(numeric, 1));
    const item = cartItems.find((i) => i.localId === localId);
    if (!item || !orderId || !item.id) return;
    setSaveError(null);
    setCartRefreshing(true);
    try {
      const updated = await updateItemInCart(orderId, item.id, { orderItemId: item.id, quantity: nextQuantity });
      setOrder(updated);
      setCartItems((updated.items ?? []).map(mapOrderItemToCartItem));
      setDirty(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to update quantity.';
      setSaveError(msg);
    } finally {
      setCartRefreshing(false);
    }
  };

  const isModalRequired = (status: OrderResponse['status'] | undefined) => {
    return Boolean(status && !['initiated', 'delivered', 'cancelled'].includes(status));
  };

  const openRemoveModal = (item: CartItem) => {
    if (!order) {
      return;
    }
    // If item is not persisted yet or status doesn't require modal, remove locally
    if (!item.id || !isModalRequired(order.status)) {
      setCartItems((prev) => prev.filter((i) => i.localId !== item.localId));
      setDirty(true);
      setSaveError(null);
      return;
    }
    setRemoveModalFor(item);
    setRemoveReason('');
    setRemoveReminderDays('');
    setRemoveSaving(false);
    setRemoveModalOpen(true);
  };

  const confirmRemoveItem = async () => {
    const item = removeModalFor;
    if (!item) return;
    setRemoveSaving(true);
    setCartRefreshing(true);
    try {
      if (orderId && item.id) {
        const updated = await removeOrderCartItem(orderId, {
          orderItemId: item.id,
          reasons: removeReason.trim() ? removeReason.trim() : null,
          reminderAfterDays: removeReminderDays.trim() ? Number(removeReminderDays) : null,
        });
        setOrder(updated);
        setCartItems((updated.items ?? []).map(mapOrderItemToCartItem));
        setDirty(false);
      }
      setSaveError(null);
      setToast({ id: Date.now(), title: 'Success', message: 'Item removed.' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to remove item.';
      setSaveError(msg);
      setToast({ id: Date.now(), title: 'Error', message: 'Failed to remove item.' });
    } finally {
      setRemoveSaving(false);
      setRemoveModalOpen(false);
      setRemoveModalFor(null);
      setCartRefreshing(false);
    }
  };

  const handleSaveAndContinue = async () => {
    if (!orderId) {
      return;
    }

    setSaving(true);
    setSaveError(null);

    const payload = cartItems.map((item, index) => {
      const subtotal = roundCurrency(item.quantity * item.unitPrice);
      const discount = roundCurrency(item.discountTotal);
      const tax = roundCurrency(item.taxTotal);
      const total = roundCurrency(subtotal - discount + tax);

      const entry: Record<string, unknown> = {
        quantity: roundQuantity(item.quantity),
        unitPrice: roundCurrency(item.unitPrice),
        lineSubtotal: subtotal,
        discountTotal: discount,
        taxTotal: tax,
        lineTotal: total,
        sortOrder: index + 1,
      };

      if (item.id) {
        entry.id = item.id;
      }

      entry.productVariantId = item.productVariantId;

      if (item.description !== null && item.description !== undefined) {
        entry.description = item.description;
      }

      if (item.itemType !== null && item.itemType !== undefined) {
        entry.itemType = item.itemType;
      }

      entry.metadata = item.metadata ?? null;
      entry.technicianId = item.technicianId ?? null;

      return entry;
    });

    try {
      const updated = await updateOrder(orderId, {
        items: payload,
        subtotalAmount: roundCurrency(itemTotals.subtotal),
        discountTotal: roundCurrency(itemTotals.discount),
        taxTotal: roundCurrency(itemTotals.tax),
        grandTotal: roundCurrency(itemTotals.total),
      });
      setOrder(updated);
      setCartItems((updated.items ?? []).map(mapOrderItemToCartItem));
      setDirty(false);
      if (onCompleted) {
        onCompleted(updated.id);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save order items.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const hasCartItems = cartItems.length > 0;
  const formattedUnits = formatQuantity(totalUnits);

  const openPromotionModal = (promotionId: string | null | undefined) => {
    if (!promotionId) {
      return;
    }
    setPromotionModalId(promotionId);
    setPromotionModalOpen(true);
  };


  return (
    <div className="space-y-6">
      <Notification
        className="flex w-80 max-w-full flex-col items-center gap-1 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 text-center text-primary shadow-lg"
        options={{
          duration: TOAST_DURATION,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true,
          close: false,
          offset: {
            x: 24,
            y: 24,
          },
        }}
        getRef={(el: NotificationElement) => {
          toastRef.current = el;
        }}
      >
        {toast ? (
          <div className="space-y-1">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">{toast.title}</p>
            <p className="text-xs text-primary/80">{toast.message}</p>
          </div>
        ) : null}
      </Notification>


      {showHeader ? (
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-slate-800">Services & products cart</h1>
            <p className="text-sm text-slate-500">
              Manage services and products associated with order{' '}
              <span className="font-semibold">{orderId ?? '—'}</span>.
            </p>
          </div>
        </div>
      ) : null}

      {orderError ? (
        <div className="rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-danger">{orderError}</div>
      ) : null}

      {orderLoading ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 mb-2">
          Loading order details…
        </div>
      ) : null}

      {!orderLoading && order ? (
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-slate-700">Add services or products</h2>
                <div className="flex items-center gap-2">
                  {searchLoading ? (
                    <span className="text-xs text-slate-400">Searching…</span>
                  ) : null}
                  {onOpenRelatedProducts ? (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={onOpenRelatedProducts}
                      disabled={!orderId}
                    >
                      Related products
                    </button>
                  ) : null}
                  {onOpenRecommendedProducts ? (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-400 transition"
                      onClick={onOpenRecommendedProducts}
                      disabled
                    >
                      Recommended products
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="relative">
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by product name, SKU, or ERP ID"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  aria-label="Search product variants"
                />
                {showVariantDropdown ? (
                  <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                    {searchLoading ? (
                      <div className="px-3 py-3 text-xs text-slate-400">Searching…</div>
                    ) : null}
                    {!searchLoading && searchError ? (
                      <div className="px-3 py-3 text-xs text-danger">{searchError}</div>
                    ) : null}
                    {!searchLoading && !searchError && variantResults.length === 0 ? (
                      <div className="px-3 py-3 text-xs text-slate-400">No product variants found.</div>
                    ) : null}
                    {!searchLoading && !searchError
                      ? variantResults.map((variant) => {
                        const details = [variant.articleNumber, variant.brand?.name]
                          .filter(Boolean)
                          .join(' · ');
                        const hasPrice =
                          variant.retailPrice !== null &&
                          variant.retailPrice !== undefined;
                        const rawStock =
                          typeof variant.erpId === 'number'
                            ? variantStockByErpId[variant.erpId]
                            : undefined;
                        const numericStock =
                          rawStock !== null &&
                          rawStock !== undefined &&
                          !Number.isNaN(Number(rawStock))
                            ? Number(rawStock)
                            : undefined;
                        const isService = variant.isCreatedByWorkshopManagement;
                        // For products, treat both a numeric stock value and an
                        // explicit null inventory value as "we have stock info".
                        const hasStockInfo =
                          !isService &&
                          (typeof numericStock === 'number' || rawStock === null);
                        // Out of stock when:
                        // - we have stock info and numeric stock is missing, or
                        // - numeric stock is defined and <= 0.
                        const isOutOfStock =
                          hasStockInfo &&
                          (!(typeof numericStock === 'number') || numericStock <= 0);

                        return (
                          <button
                            key={variant.id}
                            type="button"
                            className={`flex w-full flex-col items-start border-t border-slate-100 px-3 py-2 text-left transition first:border-t-0 ${
                              isOutOfStock
                                ? 'cursor-not-allowed opacity-60'
                                : 'hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                            }`}
                            onClick={() => {
                              if (isOutOfStock) return;
                              handleSelectVariant(variant);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-slate-700">
                                {variant.name}
                              </span>
                              <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                {isService ? 'Service' : 'Product'}
                              </span>
                            </div>
                            <span className="text-xs text-slate-400">
                              {details || 'No additional details'}
                            </span>
                            <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                              <span>UPC: {variant.upc ?? '—'}</span>
                              {/* Only show stock value / out-of-stock for regular product variants. */}
                              {hasStockInfo ? (
                                typeof numericStock === 'number' && numericStock > 0 && !isOutOfStock ? (
                                  <span>Stock: {numericStock}</span>
                                ) : (
                                  <span className="font-semibold text-danger">
                                    Out of stock
                                  </span>
                                )
                              ) : null}
                              {/* Always allow opening the detail view in inventory for both products and services. */}
                              <a
                                href={isService ? `/inventory/service-variants/${variant.id}` : `/inventory/variants/${variant.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-auto inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-100"
                                title={isService ? 'Open service variant details in new tab' : 'Open product variant stock in new tab'}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  className="mr-1 h-3 w-3"
                                >
                                  <path d="M4 3a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2V9h-2v5H4V5h5V3z" />
                                  <path d="M14 3h3a1 1 0 011 1v3h-2V5.414l-6.293 6.293-1.414-1.414L14.586 4H14V3z" />
                                </svg>
                                {isService ? 'View' : 'Stock'}
                              </a>
                            </div>
                            {hasPrice ? (
                              <span className="text-xs font-medium text-slate-500">
                                Price: {formatAmount(variant.retailPrice)}
                              </span>
                            ) : null}
                          </button>
                        );
                      })
                      : null}
                  </div>
                ) : null}
              </div>
              {recentlySelectedVariant ? (
                <div className="rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary">
                  Selected {recentlySelectedVariant.name}. Search cleared.
                </div>
              ) : null}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm w-full mt-2 mb-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 w-full">
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wide text-slate-400">Order number</p>
                <p className="text-base font-semibold text-slate-700">{order.orderNumber}</p>
              </div>

              <div className="flex flex-wrap justify-end gap-6 text-sm font-semibold text-slate-700 flex-1 sm:justify-end">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Subtotal</p>
                  <p>{formatAmount(itemTotals.subtotal)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Discounts</p>
                  <p>{formatAmount(itemTotals.discount)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Tax</p>
                  <p>{formatAmount(itemTotals.tax)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Total</p>
                  <p>{formatAmount(itemTotals.total)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm mb-2">
            
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              {/* Left side: Items and Units */}
              <div className="flex items-center gap-4 text-xs font-medium text-slate-600">
                <p>Items: {cartItems.length}</p>
                <p>Units: {formattedUnits}</p>
              </div>

              {/* Right side: Unsaved changes */}
              {dirty && (
                <p className="text-xs font-semibold text-primary">Unsaved changes</p>
              )}
            </div>

            {!hasCartItems ? (
              <div className="px-4 py-6 text-sm text-slate-500">
                No services or products linked to this order yet. Use the search above to add items.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm text-slate-700">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">#</th>
                      <th className="px-4 py-3 text-left font-semibold">Item</th>
                      <th className="px-4 py-3 text-left font-semibold">Quantity</th>
                      <th className="px-4 py-3 text-right font-semibold">Unit price</th>
                      <th className="px-4 py-3 text-right font-semibold">Subtotal</th>
                      <th className="px-4 py-3 text-right font-semibold">Discount</th>
                      <th className="px-4 py-3 text-right font-semibold">Tax</th>
                      <th className="px-4 py-3 text-right font-semibold">Total</th>
                      <th className="px-4 py-3 text-left font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {cartItems.map((item, index) => {
                      const itemSubtotal = item.quantity * item.unitPrice;
                      const itemTotal = itemSubtotal - item.discountTotal + item.taxTotal;
                      const descriptor = [
                        item.productVariant?.articleNumber ? `SKU ${item.productVariant.articleNumber}` : null,
                        item.productVariant?.brand?.name ?? null,
                        item.productVariant?.erpId ? `ERP ${item.productVariant.erpId}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ');

                      return (
                        <tr key={item.localId} className="bg-white">
                          <td className="px-4 py-3 text-xs font-semibold text-slate-500">{index + 1}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-2">
                              <div className="min-w-0">
                                <p className="font-semibold text-slate-700">
                                  {item.productVariant?.name ?? item.description ?? 'Unnamed item'}
                                </p>
                                <div className="mt-0.5 flex flex-wrap items-center gap-2">
                                  {descriptor ? <p className="text-xs text-slate-400">{descriptor}</p> : null}
                                  {item.discountTotal > 0 ? (
                                    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                      Disc: {formatAmount(item.discountTotal)}
                                    </span>
                                  ) : null}
                                  {/* Temporarily hide group badge in cart UI.
                                      Grouping logic remains active in the backend. */}
                                </div>
                              </div>
                              {(item.itemWiseDiscountId || item.cartWideDiscountId) ? (
                                <button
                                  type="button"
                                  title="Discount applied — view promotion"
                                  className="mt-0.5 inline-flex items-center rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100"
                                  onClick={() => openPromotionModal(item.itemWiseDiscountId ?? item.cartWideDiscountId)}
                                >
                                  {/* Percent tag icon */}
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="mr-1 h-3 w-3">
                                    <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V4a2 2 0 00-2-2H4zm0 2h2v2H4V4zm10 10a2 2 0 012-2h2v2a2 2 0 01-2 2h-2v-2zM8 10l6-6 2 2-6 6-2-2z" clipRule="evenodd" />
                                  </svg>
                                  Discount
                                </button>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2 py-1">
                              <button
                                type="button"
                                className="inline-flex h-5 w-5 p-5 items-center justify-center rounded-lg border border-slate-200 bg-white text-base font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                                onClick={() => handleDecreaseQuantity(item.localId)}
                                aria-label={`Decrease quantity for ${item.productVariant?.name ?? item.description ?? 'item'}`}
                              >
                                −
                              </button>

                              <input
                                type="number"
                                min={1}
                                step="1"
                                value={item.quantity}
                                onChange={(event) => handleQuantityChange(item.localId, event.target.value)}
                                className="hide-spinner h-10 w-12 rounded-lg border border-slate-200 bg-white px-2 text-center text-sm font-semibold text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/40"
                              />
                  {/* To hide the increment and decrement icon default */}
                              <style>
                                {`
                                .hide-spinner::-webkit-inner-spin-button,
                                .hide-spinner::-webkit-outer-spin-button {
                                  -webkit-appearance: none;
                                  margin: 0;
                                }
                                .hide-spinner {
                                  -moz-appearance: textfield;
                                }
                                `}
                              </style>
                              <button
                                type="button"
                                className="inline-flex h-5 w-5 p-5 items-center justify-center rounded-lg border border-slate-200 bg-white text-base font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                                onClick={() => handleIncreaseQuantity(item.localId)}
                                aria-label={`Increase quantity for ${item.productVariant?.name ?? item.description ?? 'item'}`}
                              >
                                +
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-700">{formatAmount(item.unitPrice)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-700">{formatAmount(itemSubtotal)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-700">{formatAmount(item.discountTotal)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-700">{formatAmount(item.taxTotal)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-700">{formatAmount(itemTotal)}</td>
          <td className="px-4 py-3">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg border border-danger/30 px-3 py-1.5 text-xs font-semibold text-danger transition hover:bg-danger/10"
              onClick={() => openRemoveModal(item)}
            >
              Remove
            </button>
          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      <PromotionDetailsModal
        open={promotionModalOpen}
        promotionId={promotionModalId}
        onClose={() => {
          setPromotionModalOpen(false);
          setPromotionModalId(null);
        }}
      />


      {saveError ? (
        <div className="rounded-xl border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{saveError}</div>
      ) : null}

      <div className="flex flex-wrap gap-3">
        {(onBackToOrders || onReturnToIntake) && (
          <>
            {onBackToOrders ? (
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
                onClick={onBackToOrders}
              >
                Back to order management
              </button>
            ) : null}
            {onReturnToIntake ? (
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                onClick={() => onReturnToIntake(orderId ?? null)}
              >
                Return to intake
              </button>
            ) : null}
          </>
        )}
        {!hideContinueButton && onCompleted ? (
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-primary/60"
            onClick={() => onCompleted?.(orderId!)}
            disabled={!orderId}
          >
            Continue
          </button>
        ) : null}
      </div>

      {/* Full-screen loader when adding to cart */}
      {addingToCart || cartRefreshing ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-white/60">
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow">
            Updating cart…
          </div>
        </div>
      ) : null}

      {/* Remove reason modal */}
      {removeModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl">
            <h3 className="text-base font-semibold text-slate-800">Remove item</h3>
            <p className="mt-1 text-xs text-slate-500">Optional: capture a reason and a reminder in days.</p>
            <div className="mt-3 space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Reason</label>
                <textarea
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  rows={3}
                  value={removeReason}
                  onChange={(e) => setRemoveReason(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Reminder after (days)</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={removeReminderDays}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^\d*$/.test(v)) setRemoveReminderDays(v);
                  }}
                  className="mt-1 w-40 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:border-primary hover:text-primary disabled:opacity-60"
                onClick={() => { if (!removeSaving) { setRemoveModalOpen(false); setRemoveModalFor(null); } }}
                disabled={removeSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-danger px-3 py-1.5 text-sm font-semibold text-white hover:bg-danger/90 disabled:opacity-60"
                onClick={confirmRemoveItem}
                disabled={removeSaving}
              >
                {removeSaving ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OrderProductServicesCart;
